/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Turns a parsed JUMBF manifest store into an internal model that retains raw
 * box bytes (for signature/hash verification in later steps) alongside decoded
 * claim/assertion data. This is the single source the ManifestStore mapper and
 * the verifiers both build on.
 */

import { childByType, dataChild, type JumbfBox } from '../jumbf/parseJumbf'
import { decode } from '../cbor/decode'
import { parseClaim, type ParsedClaim } from '../claim/parseClaim'

/**
 * Hard caps that bound parse + per-item digest work on an adversarial store
 * (PARSE-3). A real C2PA asset carries a handful of manifests and at most a few
 * dozen assertions per manifest; these ceilings sit far above any legitimate
 * shape while capping the O(n) hashed-URI `crypto.subtle.digest` work an attacker
 * could otherwise amplify with a large run of tiny superboxes. A store that
 * exceeds either cap is treated as malformed (fail-closed: the manifest / whole
 * store is dropped, never partially parsed). Mirrors c2pa-rs's MAX_ASSERTIONS.
 */
const MAX_MANIFESTS = 256
const MAX_ASSERTIONS = 2048

export interface ParsedAssertion {
    label: string
    /** Data-box 4CC: `cbor`, `json`, `bidb`, … */
    dataType: string
    /** Raw data-box payload bytes (used for assertion hashing). */
    raw: Uint8Array
    /** Decoded value for `cbor` assertions (else the raw bytes / parsed JSON). */
    decoded: unknown
    /**
     * Set when the data box did not decode as well-formed CBOR / conforming JSON
     * (§15.10.3.1) — the validator emits `assertion.cbor.invalid` /
     * `assertion.json.invalid` for a referenced assertion carrying this.
     */
    decodeError?: 'cbor' | 'json'
    /** Media type for embedded-file assertions (e.g. thumbnails). */
    mediaType?: string
    box: JumbfBox
}

export interface ParsedManifest {
    label: string
    claimVersion: 1 | 2
    /**
     * True when the manifest superbox is a C2PA *update* manifest (`c2um` role UUID,
     * `CAI_UPDATE_MANIFEST_UUID`) rather than a standard manifest (`c2ma`). The
     * hard-binding count rules (§15.10.1) are gated on this.
     */
    isUpdateManifest: boolean
    claim: ParsedClaim
    /** Raw claim CBOR bytes — the detached COSE payload for signature verification. */
    claimRaw: Uint8Array
    assertions: ParsedAssertion[]
    /**
     * Labels that appear on more than one assertion box in this manifest's store
     * (PARSE-1). Assertion resolution is by bare label everywhere (`find`/`Map`),
     * so a duplicate label is ambiguous: §8.4 (l.1442) mandates treating a
     * reference to an ambiguous label as *unresolved*. The validator hard-fails
     * any claim-referenced duplicate label rather than silently hashing the first
     * box while the reporter surfaces the last (a sign-vs-display differential).
     */
    duplicateAssertionLabels: Set<string>
    /** The manifest superbox and its key children, for verification. */
    box: JumbfBox
    claimBox: JumbfBox
    assertionStoreBox: JumbfBox
    signatureBox: JumbfBox | undefined
}

export interface ParsedStore {
    box: JumbfBox
    manifests: ParsedManifest[]
    byLabel: Map<string, ParsedManifest>
    /** Label of the active (most recent) manifest. */
    activeLabel: string | null
    /**
     * The JUMBF manifest-store buffer every `JumbfBox.range` indexes — needed to
     * recompute assertion hashes (§8.4.2.3). This is the C2PA box's `jumbf`
     * subarray the store was parsed from.
     */
    buffer: Uint8Array
}

function decodeAssertion (box: JumbfBox): ParsedAssertion | null {
    if (!box.label) return null
    const cbor = dataChild(box, 'cbor')
    if (cbor?.data) {
        // A malformed assertion must not crash the reader; record the well-formedness
        // failure so the validator can reject it (§15.10.3.1 assertion.cbor.invalid).
        try {
            return { label: box.label, dataType: 'cbor', raw: cbor.data, decoded: decode(cbor.data), box }
        } catch {
            return { label: box.label, dataType: 'cbor', raw: cbor.data, decoded: null, decodeError: 'cbor', box }
        }
    }
    const json = dataChild(box, 'json')
    if (json?.data) {
        let decoded: unknown = null
        let decodeError: 'json' | undefined
        try { decoded = JSON.parse(new TextDecoder().decode(json.data)) } catch { decodeError = 'json' }
        return { label: box.label, dataType: 'json', raw: json.data, decoded, decodeError, box }
    }
    // Embedded file (thumbnail) or other binary data box.
    const bidb = dataChild(box, 'bidb')
    if (bidb?.data) {
        return { label: box.label, dataType: 'bidb', raw: bidb.data, decoded: bidb.data, mediaType: box.mediaType, box }
    }
    return { label: box.label, dataType: '', raw: new Uint8Array(0), decoded: null, box }
}

function parseManifest (box: JumbfBox): ParsedManifest | null {
    if (!box.label) return null
    const claimBox = childByType(box, 'c2cl')
    const assertionStoreBox = childByType(box, 'c2as')
    if (!claimBox || !assertionStoreBox) return null

    const claimData = dataChild(claimBox, 'cbor')?.data ?? new Uint8Array(0)
    // A corrupt claim CBOR must not crash the reader (like decodeAssertion, which
    // tolerates a broken assertion): skip the manifest rather than throwing out of
    // WebCryptoReader.create. A dropped active manifest surfaces as no verifiable
    // manifest, never an uncaught exception.
    let decodedClaim: unknown
    try {
        decodedClaim = claimData.length ? decode(claimData) : {}
    } catch {
        return null
    }
    const claim = parseClaim(decodedClaim, claimBox.label)

    // Refuse to parse an assertion store with an implausible box count (PARSE-3).
    if (assertionStoreBox.children.length > MAX_ASSERTIONS) return null

    const assertions: ParsedAssertion[] = []
    for (const child of assertionStoreBox.children) {
        const a = decodeAssertion(child)
        if (a) assertions.push(a)
    }

    // PARSE-1: record labels carried by more than one assertion box (ambiguous).
    const labelCounts = new Map<string, number>()
    for (const a of assertions) labelCounts.set(a.label, (labelCounts.get(a.label) ?? 0) + 1)
    const duplicateAssertionLabels = new Set<string>()
    for (const [label, n] of labelCounts) if (n > 1) duplicateAssertionLabels.add(label)

    return {
        label: box.label,
        claimVersion: claim.version,
        isUpdateManifest: box.type === 'c2um',
        claim,
        claimRaw: claimData,
        assertions,
        duplicateAssertionLabels,
        box,
        claimBox,
        assertionStoreBox,
        signatureBox: childByType(box, 'c2cs')
    }
}

/**
 * Builds the internal model from the JUMBF root (`c2pa` manifest store).
 * The active manifest is the last one in store order (c2pa-rs convention; matches
 * the WASM oracle across all fixtures).
 */
export function parseManifestStore (root: JumbfBox, buffer: Uint8Array = new Uint8Array(0)): ParsedStore {
    const manifests: ParsedManifest[] = []
    // An implausible manifest count is malformed → fail-closed empty store, so the
    // reader yields no verifiable manifest rather than doing O(n) work (PARSE-3).
    if (root.children.length <= MAX_MANIFESTS) {
        for (const child of root.children) {
            const m = parseManifest(child)
            if (m) manifests.push(m)
        }
    }
    const byLabel = new Map(manifests.map(m => [m.label, m]))
    const activeLabel = manifests.length > 0 ? manifests[manifests.length - 1].label : null
    return { box: root, manifests, byLabel, activeLabel, buffer }
}

/** Finds a parsed assertion by label within a manifest. */
export function assertionByLabel (manifest: ParsedManifest, label: string): ParsedAssertion | undefined {
    return manifest.assertions.find(a => a.label === label)
}
